/**
 * useSatelliteLatency — loads data/satellite_latency.json and provides
 * a lookup function to get expected latency (p50) for a given satellite
 * at a given latitude.
 *
 * The JSON has the structure:
 * {
 *   meta: { lat_band_degrees: 10, ... },
 *   satellites: {
 *     "AQUA": {
 *       global: { p10, p50, p90, n },
 *       lat_bands: { "-30": { p10, p50, p90, n }, ... },
 *       hemisphere_lat_bands: {
 *         north: { "0": { p10, p50, p90, n }, ... },
 *         south: { "0": { p10, p50, p90, n }, ... }
 *       }
 *     }, ...
 *   }
 * }
 */

import { ref, readonly } from 'vue';
import { authFetch } from './useAuthFetch';

export interface LatencyStats {
  p10: number | null;
  p50: number | null;
  p90: number | null;
  n: number;
}

export interface LatencySourcePassLike {
  name: string;
  end_time: string;
  centroid_lat?: number | null;
}

export interface LatencyPassLike extends LatencySourcePassLike {
  sourcePasses?: LatencySourcePassLike[];
}

interface HemisphereLatencyBands {
  north: Record<string, LatencyStats>;
  south: Record<string, LatencyStats>;
}

interface SatelliteLatencyEntry {
  global: LatencyStats;
  lat_bands: Record<string, LatencyStats>;
  hemisphere_lat_bands?: HemisphereLatencyBands;
}

interface SatelliteLatencyData {
  meta: {
    generated_at: string;
    period_start: string;
    period_end: string;
    months: number;
    lat_band_degrees: number;
    min_samples: number;
  };
  satellites: Record<string, SatelliteLatencyEntry>;
}

/* ── Shared singleton state ── */
const data = ref<SatelliteLatencyData | null>(null);
const loaded = ref(false);
let _loadPromise: Promise<void> | null = null;

function latBandKey(lat: number, bandDeg: number): string {
  return String(Math.floor(lat / bandDeg) * bandDeg);
}

function hemisphereBandKey(
  lat: number,
  bandDeg: number,
): { hemisphere: keyof HemisphereLatencyBands; bandKey: string } {
  const signedBand = Math.floor(lat / bandDeg) * bandDeg;
  if (signedBand >= 0) {
    return { hemisphere: 'north', bandKey: String(signedBand) };
  }

  return {
    hemisphere: 'south',
    bandKey: String(Math.abs(signedBand) - bandDeg),
  };
}

/**
 * Look up the latency stats for a satellite at a given latitude.
 * Falls back to global stats if the lat band has no data.
 * Returns null if the satellite is not in the lookup table.
 */
export function getLatencyForSatellite(
  satelliteName: string,
  lat?: number | null,
): LatencyStats | null {
  const d = data.value;
  if (!d) return null;

  const entry = d.satellites[satelliteName];
  if (!entry) return null;

  if (lat != null && Number.isFinite(lat)) {
    const hemisphereBands = entry.hemisphere_lat_bands;
    if (hemisphereBands) {
      const { hemisphere, bandKey } = hemisphereBandKey(lat, d.meta.lat_band_degrees);
      const hemisphereStats = hemisphereBands[hemisphere]?.[bandKey];
      if (hemisphereStats && hemisphereStats.n > 0 && hemisphereStats.p50 != null) {
        return hemisphereStats;
      }
    }

    const bandKey = latBandKey(lat, d.meta.lat_band_degrees);
    const bandStats = entry.lat_bands[bandKey];
    if (bandStats && bandStats.n > 0 && bandStats.p50 != null) {
      return bandStats;
    }
  }

  // Fall back to global
  return entry.global;
}

function computeExpectedDeliveryMs(
  satelliteName: string,
  endTimeIso: string,
  lat?: number | null,
): number | null {
  const stats = getLatencyForSatellite(satelliteName, lat);
  if (!stats || stats.p50 == null) return null;

  const endMs = new Date(endTimeIso).getTime();
  if (!Number.isFinite(endMs)) return null;

  return endMs + stats.p50 * 60_000;
}

/**
 * Compute the expected delivery time ISO string given an overpass end time
 * and the satellite's p50 latency at the given latitude.
 * Returns null if no latency data is available.
 */
export function computeExpectedDelivery(
  satelliteName: string,
  endTimeIso: string,
  lat?: number | null,
): string | null {
  const deliveryMs = computeExpectedDeliveryMs(satelliteName, endTimeIso, lat);
  return deliveryMs == null ? null : new Date(deliveryMs).toISOString();
}

function resolveLatencySources(pass: LatencyPassLike): LatencySourcePassLike[] {
  if (Array.isArray(pass.sourcePasses) && pass.sourcePasses.length > 0) {
    return pass.sourcePasses;
  }
  return [pass];
}

function computeLatestExpectedDeliveryMsForPass(pass: LatencyPassLike): number | null {
  let latestDeliveryMs = Number.NEGATIVE_INFINITY;
  let hasDelivery = false;

  for (const sourcePass of resolveLatencySources(pass)) {
    const deliveryMs = computeExpectedDeliveryMs(
      sourcePass.name,
      sourcePass.end_time,
      sourcePass.centroid_lat,
    );
    if (deliveryMs == null || !Number.isFinite(deliveryMs)) continue;
    latestDeliveryMs = Math.max(latestDeliveryMs, deliveryMs);
    hasDelivery = true;
  }

  return hasDelivery ? latestDeliveryMs : null;
}

/**
 * Expected delivery is a forward-looking promise, so it is only meaningful while
 * it is still ahead of us.  Now that the time range reaches into the past, a pass
 * from last week would otherwise advertise a delivery that already came and went.
 * A pass that just happened and whose data is still in transit keeps it — that is
 * exactly the case worth showing.  Returning null here means all six display sites
 * inherit the rule without repeating it.
 */
export function computeExpectedDeliveryForPass(pass: LatencyPassLike): string | null {
  const deliveryMs = computeLatestExpectedDeliveryMsForPass(pass);
  if (deliveryMs == null || deliveryMs < Date.now()) return null;
  return new Date(deliveryMs).toISOString();
}

/**
 * For grouped passes, compute latency from the grouped bar's end time to the
 * latest expected delivery among its constituent source passes.
 */
export function getExpectedLatencyMinutesForPass(pass: LatencyPassLike): number | null {
  const deliveryMs = computeLatestExpectedDeliveryMsForPass(pass);
  // Same rule as computeExpectedDeliveryForPass: once delivery has elapsed there
  // is nothing left to expect, so the figure is dropped rather than shown stale.
  if (deliveryMs == null || deliveryMs < Date.now()) return null;

  const endMs = new Date(pass.end_time).getTime();
  if (!Number.isFinite(endMs)) return null;

  return Math.max(0, (deliveryMs - endMs) / 60_000);
}

async function loadLatencyData(): Promise<void> {
  try {
    const resp = await authFetch('/data/satellite_latency.json');
    if (!resp.ok) {
      console.warn(`[useSatelliteLatency] Failed to load latency data: ${resp.status}`);
      return;
    }
    data.value = await resp.json();
  } catch (err) {
    console.warn('[useSatelliteLatency] Failed to load latency data:', err);
  } finally {
    loaded.value = true;
  }
}

export function useSatelliteLatency() {
  if (!_loadPromise) {
    _loadPromise = loadLatencyData();
  }

  return {
    latencyData: readonly(data),
    latencyLoaded: readonly(loaded),
    getLatencyForSatellite,
    computeExpectedDelivery,
    computeExpectedDeliveryForPass,
    getExpectedLatencyMinutesForPass,
  };
}
